import { useState } from "react";

import { ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";

import { IPart } from "../../models/Part";
import formatDateTime from "../../utils/formatDateTime";
import { IListingOptimisation } from "../../models/listingOptimisation/IListingOptimisation";
import classNames from "../../utils/classNames";
import UnlistedKTypeUpdate from "../ebayListings/unlistedTable/UnlistedKTypeUpdate";
import { MIN_IMAGE_COUNT } from "../ebayListings/listedTable/listingScore";
import { IQueueRowSignals } from "./queueRowSignals";
import { DONE_SECTION_CLASSES } from "./drawerSectionState";
import DoneChip from "./DoneChip";
import PartProLookupButton from "../shared/PartProLookupButton";
import { scoreToneClasses } from "./QueueRow";

// Only the MPN is typed in. The OE number is read-only everywhere: the PartPro lookup sets it.
export type PartNumberField = "partNumber";

interface IFocusDrawerHealthSectionProps {
  part: IPart;
  signals: IQueueRowSignals;
  optimisation: IListingOptimisation | undefined;
  onSavePartNumber: (field: PartNumberField, value: string) => void;
  savingPartNumber: boolean;
  onManagePhotos: () => void;
  onOpenSpecifics: () => void;
  onChangePolicies: () => void;
  onRegenerateDescription: () => void;
  regenerating: boolean;
  onSetGrade: () => void;
  /** Nothing fixable is missing and nothing blocks the relist: the section turns green. */
  done: boolean;
  /** The score was worked out with the pending price applied, not the live one. */
  includesChanges?: boolean;
}

type LineTone = "warn" | "blocked" | "ok";

const LINE_CLASSES: Record<LineTone, string> = {
  warn: "border-amber-200 bg-amber-50 text-amber-900",
  blocked: "border-red-200 bg-red-50 text-red-800",
  ok: "border-gray-200 bg-white text-gray-700",
};

const BUTTON_CLASSES: Record<LineTone, string> = {
  warn: "border-amber-300 bg-white text-amber-900 hover:bg-amber-100",
  blocked: "border-red-300 bg-white text-red-800 hover:bg-red-100",
  ok: "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
};

function Line({ tone, children, action }: { tone: LineTone; children: React.ReactNode; action?: React.ReactNode }) {
  const Icon = tone === "ok" ? CheckCircleIcon : tone === "blocked" ? XCircleIcon : ExclamationTriangleIcon;
  return (
    <li className={classNames("flex items-center gap-2.5 rounded-md border px-2.5 py-2 text-xs", LINE_CLASSES[tone])}>
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1">{children}</span>
      {action}
    </li>
  );
}

function LineButton({ tone, onClick, children, disabled }: { tone: LineTone; onClick: () => void; children: React.ReactNode; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={classNames("inline-flex h-7 shrink-0 items-center gap-1 rounded-md border px-2.5 text-[11px] font-semibold disabled:opacity-50", BUTTON_CLASSES[tone])}
    >
      {children}
    </button>
  );
}

function PartNumberInput({
  label,
  field,
  initialValue,
  onSave,
  saving,
}: {
  label: string;
  field: PartNumberField;
  initialValue: string;
  onSave: (field: PartNumberField, value: string) => void;
  saving: boolean;
}) {
  const [value, setValue] = useState(initialValue);
  const trimmed = value.trim();
  const changed = trimmed !== initialValue.trim();
  const tone = initialValue ? "slate" : "amber";

  return (
    <form
      className="flex shrink-0 items-center gap-1"
      onSubmit={(e) => {
        e.preventDefault();
        if (trimmed && changed) onSave(field, trimmed);
      }}
    >
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={label}
        aria-label={label}
        className={classNames(
          "h-7 w-36 rounded-md border px-2 text-xs focus:ring-0",
          tone === "amber" ? "border-amber-300 focus:border-amber-500" : "border-slate-300 focus:border-primary-500"
        )}
      />
      <button
        type="submit"
        disabled={!trimmed || !changed || saving}
        className={classNames(
          "inline-flex h-7 items-center rounded-md border bg-white px-2.5 text-[11px] font-semibold disabled:opacity-50",
          tone === "amber" ? "border-amber-300 text-amber-900 hover:bg-amber-100" : "border-slate-300 text-slate-700 hover:bg-slate-50"
        )}
      >
        {saving ? <ArrowPathIcon className="h-3 w-3 animate-spin" aria-hidden="true" /> : initialValue ? "Save" : "Add"}
      </button>
    </form>
  );
}

/**
 * The listing score as a checklist, one line per thing to fix with the button that fixes it.
 * Part numbers, KType and grade save to the part straight away; photos, specifics and policies
 * open their editors; the description regenerates from the category template on the part only.
 */
export default function FocusDrawerHealthSection({
  part,
  signals,
  optimisation,
  onSavePartNumber,
  savingPartNumber,
  onManagePhotos,
  onOpenSpecifics,
  onChangePolicies,
  onRegenerateDescription,
  regenerating,
  onSetGrade,
  done,
  includesChanges = false,
}: IFocusDrawerHealthSectionProps) {
  const { score } = signals;
  const missing = new Set(score.missing);
  const lookupRun = !!part.lastPartLookupChecked;
  // Everything the score marks down, including the criteria that have no line here (age, price,
  // make / model / VIN), so "8 of 10" is never a mystery.
  const notCounted = score.breakdown.filter((field) => field.applicable && !field.present).map((field) => field.label);
  const imageCount = signals.imageCount ?? 0;
  const bgStaged = !!optimisation?.removeBackgroundFirstImage;

  const aspectValue = (name: string) => part.aspects?.find((aspect) => aspect.name?.toLowerCase() === name.toLowerCase())?.value;
  const specificsSummary = [
    part.vehicle?.kType ? `KType ${part.vehicle.kType}` : null,
    aspectValue("Brand") ? `Brand ${aspectValue("Brand")}` : null,
    part.partNumber ? `MPN ${part.partNumber}` : null,
  ].filter(Boolean);

  const offer = part.offers?.[0];
  const hasPolicies = !!(offer?.fulfillmentPolicyId && offer?.paymentPolicyId && offer?.returnPolicyId);
  const policiesStaged = !!(optimisation?.newFulfillmentPolicyId || optimisation?.newPaymentPolicyId || optimisation?.newReturnPolicyId || optimisation?.newLocationKey);

  return (
    <section aria-label="Listing health" className={classNames("space-y-2 border-b px-6 py-4", done ? DONE_SECTION_CLASSES : "border-gray-200")}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="inline-flex items-center gap-2 text-[13px] font-semibold text-gray-900">
          Listing health
          {done ? <DoneChip /> : null}
        </h3>
        <span className="inline-flex items-center gap-1.5">
          <span className={classNames("rounded-full px-2 py-0.5 text-[13px] font-bold", scoreToneClasses(score.percent))}>{score.percent}%</span>
          <span className="text-[11px] text-gray-500">
            {score.present} of {score.total}
            {includesChanges ? " · with your price" : ""}
          </span>
        </span>
      </div>
      {notCounted.length > 0 && (
        <p className="text-[11px] text-gray-500">
          Not counted: {notCounted.join(" · ")}
        </p>
      )}

      <ul className="space-y-1">
        {signals.relistBlocked &&
          signals.relistErrors.map((error) => (
            <Line
              key={error}
              tone="blocked"
              action={
                /image/i.test(error) ? (
                  <LineButton tone="blocked" onClick={onManagePhotos}>Manage photos</LineButton>
                ) : /polic|location/i.test(error) ? (
                  <LineButton tone="blocked" onClick={onChangePolicies}>Change</LineButton>
                ) : (
                  <LineButton tone="blocked" onClick={onOpenSpecifics}>Specifics</LineButton>
                )
              }
            >
              <strong>Can&apos;t relist</strong> — {error}
            </Line>
          ))}

        {/* The MPN is always editable here; the key is the part id so a different listing gets a fresh box.
            With no MPN and no PartPro lookup yet, the lookup is offered first - it usually finds one. */}
        <Line
          tone={missing.has("MPN") ? "warn" : "ok"}
          action={
            <span className="flex shrink-0 items-center gap-1.5">
              {missing.has("MPN") && !lookupRun ? <PartProLookupButton part={part} /> : null}
              <PartNumberInput key={part.id} label="MPN" field="partNumber" initialValue={part.partNumber ?? ""} onSave={onSavePartNumber} saving={savingPartNumber} />
            </span>
          }
        >
          <strong>MPN</strong>
          {missing.has("MPN") ? ` missing${!lookupRun ? " · try the PartPro lookup" : ""}` : ` ${part.partNumber}`}
        </Line>
        {/* The OE number only ever comes from the PartPro lookup and is never held against the listing here */}
        <Line tone="ok" action={!lookupRun ? <PartProLookupButton part={part} /> : undefined}>
          <strong>OE part number</strong>
          {part.oePartNumber ? ` ${part.oePartNumber}` : " —"}
          <span className="block text-[11px] opacity-80">
            {lookupRun
              ? `PartPro lookup ${formatDateTime(part.lastPartLookupChecked, DateTime.DATE_MED)}${part.oePartNumber ? "" : " · none found"}`
              : "PartPro lookup not run yet"}
          </span>
        </Line>
        {missing.has("KType") && (
          <Line tone="warn" action={part.vehicle ? <div className="w-44 shrink-0"><UnlistedKTypeUpdate part={part} /></div> : undefined}>
            <strong>KType</strong> missing
          </Line>
        )}
        {missing.has("Brand") && (
          <Line tone="warn" action={<LineButton tone="warn" onClick={onOpenSpecifics}>Specifics</LineButton>}>
            <strong>Brand</strong> missing
          </Line>
        )}
        {missing.has("Grade") && (
          <Line tone="warn" action={<LineButton tone="warn" onClick={onSetGrade}>Set grade</LineButton>}>
            <strong>Grade</strong> missing
          </Line>
        )}
        {(missing.has("Make") || missing.has("Model") || missing.has("VIN")) && (
          <Line tone="warn">
            <strong>Vehicle</strong> is missing {["Make", "Model", "VIN"].filter((key) => missing.has(key)).join(", ")} — set on the vehicle record
          </Line>
        )}

        <Line
          tone={imageCount > MIN_IMAGE_COUNT ? "ok" : "warn"}
          action={<LineButton tone={imageCount > MIN_IMAGE_COUNT ? "ok" : "warn"} onClick={onManagePhotos}>Manage photos</LineButton>}
        >
          <strong>
            {imageCount} photo{imageCount === 1 ? "" : "s"}
          </strong>
          {imageCount > MIN_IMAGE_COUNT ? "" : ` — eBay rewards ${MIN_IMAGE_COUNT} or more`}
          {bgStaged ? "; background removal staged for the 1st" : ""}
        </Line>

        <Line tone="ok" action={<LineButton tone="ok" onClick={onOpenSpecifics}>Specifics</LineButton>}>
          {specificsSummary.length > 0 ? specificsSummary.join(", ") : "Item specifics"}
        </Line>

        <Line
          tone={hasPolicies || policiesStaged ? "ok" : "warn"}
          action={<LineButton tone={hasPolicies || policiesStaged ? "ok" : "warn"} onClick={onChangePolicies}>Change</LineButton>}
        >
          <strong>Policies</strong> {policiesStaged ? "— new policies staged" : hasPolicies ? "set on the listing" : "fall back to the category defaults"}
        </Line>

        <Line tone="ok" action={<LineButton tone="ok" onClick={onRegenerateDescription} disabled={regenerating}>{regenerating ? "Regenerating…" : "Regenerate"}</LineButton>}>
          <strong>Description</strong> from the {part.category?.name ?? "category"} template — regenerates on the part, goes live at relist
        </Line>
      </ul>
    </section>
  );
}
